import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { PlusIcon } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { CategoryPicker } from "@/components/portal/lms/CategoryPicker";
import { CreateTestDialog } from "@/components/portal/lms/CreateTestDialog";
import { CuratorGate } from "@/components/portal/lms/CuratorGate";
import { TestAuthoringPanel } from "@/components/portal/lms/TestAuthoringPanel";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useLmsCuratorAccess } from "@/hooks/useLmsCuratorAccess";
import type { CategoryHierarchyNode } from "@/services/api/categories";

export const Route = createFileRoute("/portal/lms/author")({
  component: LmsAuthorPage,
});

/**
 * Author tests (`/portal/lms/author`). Pick a category the caller curates,
 * create a draft (title, pass mark, time limit), attach approved questions, and
 * publish. Drafts survive a reload: they are listed from the server. The picker
 * lists only categories `useLmsCuratorAccess` admits; dp lms's per-category
 * curator gate enforces every write.
 */
function LmsAuthorPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const queryClient = useQueryClient();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const access = useLmsCuratorAccess();
  const { canCurate } = access;
  const curatorFilter = useCallback((node: CategoryHierarchyNode) => canCurate(node.id), [canCurate]);

  return (
    <div className="space-y-4" data-testid="lms-author-page">
      <Helmet>
        <title>Author tests | LMS</title>
      </Helmet>
      <PageHeader
        title="Author tests"
        description="Create a draft test in a category, attach approved questions, and publish it."
      />

      <CuratorGate access={access}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <CategoryPicker
            token={token ?? ""}
            orgId={orgId}
            value={categoryId}
            onChange={setCategoryId}
            filter={curatorFilter}
            emptyMessage="You don't curate any categories."
          />
          {categoryId && (
            <Button onClick={() => setCreating(true)} data-testid="lms-author-create">
              <PlusIcon className="mr-2 h-4 w-4" /> New test
            </Button>
          )}
        </div>

        {categoryId ? (
          <TestAuthoringPanel key={categoryId} token={token ?? ""} categoryId={categoryId} />
        ) : (
          <p className="text-sm text-muted-foreground">Select a category to see its tests.</p>
        )}

        <CreateTestDialog
          token={token ?? ""}
          categoryId={categoryId}
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={(test) => {
            setCreating(false);
            toast.success(`Draft "${test.title}" created.`);
            queryClient.invalidateQueries({ queryKey: ["lms", "tests", categoryId] });
          }}
        />
      </CuratorGate>
    </div>
  );
}
